import { ITextFieldManager } from "./../../textfield/manager/ITextFieldManager";
import { GoodgameTextFieldManager } from "./../../textfield/manager/GoodgameTextFieldManager";
import { MathBase } from "./../../math/MathBase";
import { IBasicGGSTextField } from "./../../textfield/textfield/IBasicGGSTextField";
import { BasicToolTipEvent } from "../event/BasicToolTipEvent";
import { TextField } from "@goodgamestudios/createjs-utilities";
import { getLogger } from "@goodgamestudios/logger";
import DisplayObject = createjs.DisplayObject;
import MovieClip = createjs.MovieClip;
import Sprite = createjs.Sprite;
import MouseEvent = createjs.MouseEvent;
import Point = createjs.Point;
import Rectangle = createjs.Rectangle;
import Container = createjs.Container;

const logger = getLogger("CoreJS.BasicToolTipManager");

export class BasicToolTipManager {
    public static TOOLTIP_LABEL: string = "toolTipText";
    //public static const TOOLTIP_VO:String = "toolTipVO";

    protected TEXT_BORDER: number = 30;
    protected ARROW_HEIGHT: number = 8;
    protected ARROW_OFFSET: number = 5;
    protected TIP_SPACE: number = 5;
    protected MIN_WIDTH: number = 60;
    protected _max_width: number = 200;

    protected textFieldHeightAdditive: number = 5;
    protected textFieldWidthAdditive: number = 5;
    protected backGroundHeightAdditive: number = 0;

    protected _standardTextFieldY: number;
    protected _target: DisplayObject;
    protected _tooltipLayer: Container;
    protected _disp: Container;
    protected _tooltipBody: MovieClip;
    protected _tooltipArrow: MovieClip;
    protected _background: Container;
    protected _textField: TextField;
    protected _ggsTextField: IBasicGGSTextField;

    protected toolTipHeight: number;

    /**
     * init Tooltip and added to layer
     */
    constructor(layer: Container, tooltipBodyClass: any, tooltipArrowClass: any) {
        this._tooltipLayer = layer;
        this._tooltipLayer.mouseChildren = false;
        this._tooltipLayer.mouseEnabled = false;
        this._disp = new Container();
        this._tooltipLayer.addEventListener(BasicToolTipEvent.TOOLTIP_HIDE, this.bindFunction(this.onHide));
        this._tooltipBody = new tooltipBodyClass();
        this._tooltipArrow = new tooltipArrowClass();
        this._disp.mouseChildren = false;
        this._disp.mouseEnabled = false;
        this._disp.addChild(this._tooltipArrow);
        this._disp.addChild(this._tooltipBody);
        this._tooltipLayer.addChild(this._disp);
        this._textField = this.tooltipBody.txt_label;
        this._standardTextFieldY = this._textField.y;
        this._background = this.tooltipBody.background;
        this.updateTextField();
        this.hide();
        this.toolTipHeight = this._disp.height;

    }


    /**
     * remove eventlistener
     */
    public destroy(): void {
        this._tooltipLayer.removeEventListener(BasicToolTipEvent.TOOLTIP_HIDE, this.bindFunction(this.onHide));
    }

    /**
     * set to standardposition and check textformat for current language
     */
    protected updateTextField(): void {
        logger.debug("updateTextField");
        this._textField.y = this._standardTextFieldY;
    }

    private onHide(event: BasicToolTipEvent): void {
        this.hide();
    };

    /**
     * set tooltiptext and tooltipsize by text
     * set position by target position
     */
    public show(toolTipText: Object, target: DisplayObject = null): void {
        // TODO HTML5  check TOOLTIP SHOW    A LOT OF STUFF IS GOING ON..
        logger.debug("show tooltip ");
        if (toolTipText == null) {
            return;
        }
        this.updatePosition();

        this._ggsTextField ? this._ggsTextField.width = this._max_width : this._textField.width = this._max_width;
        this._ggsTextField ? this._ggsTextField.height = 2000 : this._textField.height = 2000;

        this._target = target;

        if (this._textField && this._background) {
            this.assignToolTipText(toolTipText);

            if (this._ggsTextField.multiline) {
                this._ggsTextField.width = this._max_width;
            }

            this._ggsTextField.width = this._ggsTextField.textWidth + this.textFieldWidthAdditive;
            this._ggsTextField.height = this._ggsTextField.textHeight + this.textFieldHeightAdditive;
            this._ggsTextField.x = -(this._ggsTextField.width * 0.5);
            this._ggsTextField.y = -this._ggsTextField.height;

            this._background.width = Math.max(this.MIN_WIDTH, this._ggsTextField.width + this.TEXT_BORDER);

            if (this._ggsTextField.multiline) {
                this._background.height = this._ggsTextField.height + this.backGroundHeightAdditive;
            }
            this.toolTipHeight = this._background.height;
        }

        let halfTipWidth: number = this._background.width * 0.5;
        let globalPos: Point = this._target.parent.localToGlobal(this._target.x, this._target.y);
        let tipX: number = globalPos.x;
        let tipY: number = globalPos.y;

        this._tooltipArrow.scaleY = 1;

        if (tipX + halfTipWidth > this._disp.stage.stageWidth) {
            this._disp.x = this._disp.stage.stageWidth - halfTipWidth - this.TIP_SPACE;
            this._tooltipArrow.x = MathBase.min(Number(this._background.width * 0.5 - 10), Number(this._disp.globalToLocal(globalPos.x, 0).x));
        }
        else {
            if (tipX - halfTipWidth < 0) {
                this._disp.x = halfTipWidth + this.TIP_SPACE;
                this._tooltipArrow.x = this._disp.globalToLocal(globalPos.x, 0).x;
            }
            else {
                this._disp.x = tipX;
                this._tooltipArrow.x = 0;
            }
        }

        // the bounds of an object don't change if the object is scaled. See
        // http://timetoflash.blogspot.kr/2012/01/getbounds-wrong-results.html

        let scaledBounds: Rectangle = this._target.getBounds();
        scaledBounds.bottom *= this._target.scaleY;
        scaledBounds.top *= this._target.scaleY;

        if (tipY + (scaledBounds.top - this.toolTipHeight - this.ARROW_OFFSET - this.ARROW_HEIGHT) * this._disp.scaleY < 0) {

            tipY = this.flipHorizontalToolTip(this.toolTipHeight, scaledBounds, globalPos);
        }
        else {
            tipY += (scaledBounds.top - this.ARROW_HEIGHT - this.ARROW_OFFSET) * this._disp.scaleY;
            this._tooltipArrow.y = 0;
        }
        this._disp.y = tipY;
        this._disp.visible = true;

        this._target.addEventListener("mousedown", this.bindFunction(this.onMouseDown));
    }

    /**
     * function for assigning toolTipText, could be overwritten (because of usage of textfieldmanagers)
     * @param toolTipText
     *
     */
    protected assignToolTipText(toolTipText: Object): void {
        // TODO: @HTML5 - Type 'LocalizedTextVO' is not assignable to type 'AbstractTextContentVO'. Types have separate declarations of a private property '_propertyChangedSignal'.
        /*if (this._ggsTextField != null) {
         this._ggsTextField.textContentVO = new LocalizedTextVO(String(toolTipText));
         }
         else {
         this._ggsTextField = this.textFieldManager.registerTextField(this._textField, new LocalizedTextVO(String(toolTipText)));
         }*/
    }

    protected flipHorizontalToolTip(ttHeight: number, buttonBounds: Rectangle, globalPos: Point): number {
        this._tooltipArrow.scaleY = -1;
        this._tooltipArrow.y = -this.toolTipHeight;
        return globalPos.y + (buttonBounds.bottom + ttHeight + this.ARROW_OFFSET + this.ARROW_HEIGHT) * this._disp.scaleY;
    }

    /*public function showAdvancedTooltip(vo:BasicTooltipVO, target:DisplayObject = null):void
     {
     }*/

    public updatePosition(): void {
    }

    public hideAdvancedTooltip(): void {
    }

    protected onMouseDown(event: MouseEvent): void {
        this.hide();
    };

    public get disp(): Container {
        return this._disp;
    }

    public hide(): void {
        this._disp.visible = false;

        if (this._target) {
            this._target.removeEventListener("mousedown", this.bindFunction(this.onMouseDown));
        }
    }

    protected get textFieldManager(): ITextFieldManager {
        return GoodgameTextFieldManager.getInstance();
    }

    private get tooltipBody(): any {
        return this._tooltipBody as any;
    }
}

